/**
 * AI Insights API — NestJS REST for dashboard markdown insights.
 *
 * Endpoint: POST /api/v1/ai-insights/dashboard
 * Auth: Bearer JWT via NestClient (NEST_BASE_URL from apiEndpoints.js)
 * Company + financial year come from JWT context — do not send in body.
 *
 * Body: { tab, start_date, end_date }
 * - tab: business_overview | profit_and_loss | balance_sheet | receivables | payables
 * - start_date / end_date: dd/mm/yyyy (IST), end_date >= start_date
 * Response 200: { tab, period_label, markdown_summary }
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { client } from '../../../api/NestClient';
import { nestBaseQuery } from '../../../utils/nestBaseQuery';
import type { paths } from '../../../api/types/api';

// -----------------------------------------------------------------------------
// Types (from OpenAPI — api.ts)
// -----------------------------------------------------------------------------

/** POST /api/v1/ai-insights/dashboard request body */
export type AiInsightsDashboardRequestDto =
  paths['/api/v1/ai-insights/dashboard']['post']['requestBody']['content']['application/json'];

/** 200 response — { tab, period_label, markdown_summary } */
export type AiInsightsDashboardResponseDto =
  paths['/api/v1/ai-insights/dashboard']['post']['responses']['200']['content']['application/json'];

// -----------------------------------------------------------------------------
// API definition & export
// -----------------------------------------------------------------------------

/** Wraps Nest failures so callers can branch on HTTP status (e.g. 500 retry message). */
export class AiInsightsApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AiInsightsApiError';
    this.status = status;
  }
}

export const aiInsightsApi = createApi({
  reducerPath: 'aiInsightsApi',
  baseQuery: nestBaseQuery,
  endpoints: (builder) => ({
    /** RTK mutation — generates markdown insights for the active dashboard tab. */
    generateDashboardInsights: builder.mutation<AiInsightsDashboardResponseDto, AiInsightsDashboardRequestDto>({
      query: (body) =>
        client.POST('/api/v1/ai-insights/dashboard', {
          body,
        }),
    }),
  }),
});

export const { useGenerateDashboardInsightsMutation } = aiInsightsApi;

/**
 * Imperative call used by DashboardHome / BizView (same pattern as importAccounts).
 * On failure throws AiInsightsApiError with Nest status + message.
 */
export async function generateDashboardInsights(
  body: AiInsightsDashboardRequestDto
): Promise<AiInsightsDashboardResponseDto> {
  const result = await nestBaseQuery<AiInsightsDashboardResponseDto>(
    client.POST('/api/v1/ai-insights/dashboard', { body })
  );

  if ('error' in result) {
    throw new AiInsightsApiError(result.error.message, result.error.status);
  }

  return result.data;
}
